import { AlertCircle, Ban } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Callout } from '@/components/callouts/Callout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Spinner,
  Text,
  TextArea,
} from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

import { AffiliateTaxStatus, TAX_STATUS_DISPLAY } from '../domain/types';
import { useAffiliateTaxInfo, useRevokeAffiliateTaxInfo } from '../hooks/useAffiliateTaxInfo';

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userIdentifier: string;
  /** Derived tax status surfaced in the table. Used as a hint while the detail query loads. */
  taxStatus: AffiliateTaxStatus;
}

const FORM_TYPE_LABEL: Record<'w9' | 'w8ben' | 'w8bene', string> = {
  w9: 'W-9 (US person)',
  w8ben: 'W-8BEN (Foreign individual)',
  w8bene: 'W-8BEN-E (Foreign entity)',
};

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (value == null || value === '') return null;
  return (
    <div className="tw:flex tw:flex-col tw:gap-1">
      <Label className="tw:text-xs tw:text-muted-foreground">{label}</Label>
      <Text variant="body-sm">{value}</Text>
    </div>
  );
}

export function AffiliateTaxInfoModal({ open, onClose, projectId, userIdentifier, taxStatus }: Props) {
  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);
  const [revokedReason, setRevokedReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  // Synchronous guard: prevents a double-click between `revoke()` and the
  // subsequent re-render with `isPending=true` from firing twice.
  const inFlightRef = useRef(false);

  const { data: taxInfo, isLoading, isError } = useAffiliateTaxInfo(projectId, userIdentifier);
  const { mutate: revoke, isPending: isRevoking } = useRevokeAffiliateTaxInfo(projectId, userIdentifier);

  useEffect(() => {
    if (!open) {
      setRevokeConfirmOpen(false);
      setRevokedReason('');
      setReasonError(null);
      inFlightRef.current = false;
    }
  }, [open]);

  const handleRevokeSubmit = () => {
    if (inFlightRef.current) return;
    const trimmed = revokedReason.trim();
    if (!trimmed) {
      setReasonError('A reason is required when revoking tax info.');
      return;
    }
    setReasonError(null);
    inFlightRef.current = true;
    revoke(
      { revoked_reason: trimmed },
      {
        onSuccess: () => {
          inFlightRef.current = false;
          setRevokeConfirmOpen(false);
          setRevokedReason('');
        },
        onError: () => {
          inFlightRef.current = false;
        },
      },
    );
  };

  // Show Revoke once the detail loaded and the record isn't already revoked.
  // Authorization is enforced server-side; no FE role gate here.
  const canShowRevoke = !!taxInfo && taxInfo.status !== 'revoked';

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Affiliate tax information</DialogTitle>
            <DialogDescription>
              Sensitive fields are masked. Revoking tax info forces the affiliate to resubmit before claiming rewards.
            </DialogDescription>
          </DialogHeader>

          <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6 tw:pt-0">
            <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
              <Badge variant={TAX_STATUS_DISPLAY[taxStatus].variant} size="sm">
                {TAX_STATUS_DISPLAY[taxStatus].label}
              </Badge>
              {!isLoading && taxInfo?.expires_at && (
                <Badge variant="outline" size="sm">
                  Expires {taxInfo.expires_at}
                </Badge>
              )}
            </div>

            {isLoading && (
              <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
                <Spinner size="xs" />
                <Text variant="body-sm" color="muted">
                  Loading tax details…
                </Text>
              </div>
            )}

            {!isLoading && isError && (
              <Callout type="error" title="Couldn't load tax info" body="Please try again in a moment." />
            )}

            {!isLoading && !isError && !taxInfo && (
              <Callout
                type="info"
                title="No tax info submitted"
                body="This affiliate hasn't submitted a W-9 or W-8 yet."
              />
            )}

            {taxInfo && (
              <div className={cn('tw:grid tw:grid-cols-2 tw:gap-4')}>
                <InfoRow label="Form type" value={FORM_TYPE_LABEL[taxInfo.form_type]} />
                <InfoRow label="Legal name" value={taxInfo.legal_name} />
                <InfoRow label="Business name" value={taxInfo.business_name} />
                <InfoRow label="Tax classification" value={taxInfo.tax_classification} />
                <InfoRow label="Tax ID" value={taxInfo.tax_id_number_masked} />
                <InfoRow label="Foreign tax ID" value={taxInfo.foreign_tax_id_masked} />
                <InfoRow label="Country" value={taxInfo.country} />
                <InfoRow label="Country of citizenship" value={taxInfo.country_of_citizenship} />
                <InfoRow label="Country of incorporation" value={taxInfo.country_of_incorporation} />
                <InfoRow label="Entity type" value={taxInfo.entity_type} />
                <InfoRow label="Address" value={taxInfo.address_line1} />
                <InfoRow label="Address line 2" value={taxInfo.address_line2} />
                <InfoRow label="City" value={taxInfo.city} />
                <InfoRow label="State" value={taxInfo.state} />
                <InfoRow label="ZIP" value={taxInfo.zip} />
                <InfoRow label="Treaty country" value={taxInfo.treaty_country} />
                <InfoRow label="Treaty article" value={taxInfo.treaty_article} />
                <InfoRow label="Date of birth" value={taxInfo.date_of_birth} />
                <InfoRow label="Signed on" value={taxInfo.signature_date} />
                <InfoRow label="Submitted on" value={taxInfo.created_at?.split('T')[0]} />
                {taxInfo.status === 'revoked' && (
                  <div className="tw:col-span-2">
                    <InfoRow label="Revocation reason" value={taxInfo.revoked_reason} />
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            {canShowRevoke && (
              <Button variant="destructive" onClick={() => setRevokeConfirmOpen(true)}>
                <Ban size={16} />
                Revoke tax info
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={revokeConfirmOpen} onOpenChange={setRevokeConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="tw:flex tw:items-center tw:gap-2">
              <AlertCircle size={18} className="tw:text-error-main" />
              Revoke tax info
            </AlertDialogTitle>
            <AlertDialogDescription>
              The affiliate will need to resubmit tax info before they can claim rewards. This action is logged.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="tw:flex tw:flex-col tw:gap-2 tw:my-4">
            <Label htmlFor="revoked_reason">Reason (required)</Label>
            <TextArea
              id="revoked_reason"
              value={revokedReason}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setRevokedReason(e.target.value);
                if (reasonError && e.target.value.trim()) setReasonError(null);
              }}
              placeholder="e.g. Submitted info doesn't match KYC records"
              rows={3}
              disabled={isRevoking}
            />
            {reasonError && (
              <Text variant="body-sm" color="error">
                {reasonError}
              </Text>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRevoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevokeSubmit} disabled={isRevoking}>
              {isRevoking ? <Spinner size="xs" /> : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
